import { Bookmark, BookmarkCheck, MapPin, Wifi } from "lucide-react";
import { CompanyLogo } from "@/components/Common";
import { AtsBadge } from "@/components/AtsBadge";
import { TailorResumeButton } from "@/components/TailorResumeButton";
import { FindPeopleButton } from "@/components/people/FindPeopleButton";
import { cn } from "@/lib/utils";

const STATUS_STYLE = {
  saved: "text-[#F3C649] border-[#D4AF37]/40",
  applied: "text-sky-300 border-sky-500/40",
  interviewing: "text-violet-300 border-violet-500/40",
  offer: "text-emerald-300 border-emerald-500/40",
  rejected: "text-red-300 border-red-500/40",
};

export const StatusBadge = ({ status }) =>
  status ? <span className={cn("rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider", STATUS_STYLE[status])}>{status}</span> : null;

export const JobCard = ({ job, onOpen, onToggleSave, isNew, index = 0, tailor = false, findPeople = false }) => (
  <article onClick={() => onOpen(job)} data-testid={`job-card-${job.id}`}
    className="group fade-up cursor-pointer rounded-xl border border-[#27272A] bg-[#18181B] p-5 transition-[border-color,transform,background-color] hover:-translate-y-0.5 hover:border-[#D4AF37]/40 hover:bg-[#1c1c20]"
    style={{ animationDelay: `${Math.min(index, 10) * 40}ms` }}>
    <div className="flex gap-4">
      <CompanyLogo src={job.logo} name={job.company} />
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="truncate font-display text-base font-semibold text-zinc-100 group-hover:text-[#F3C649]">{job.title}</h3>
            <p className="truncate text-sm text-zinc-400">{job.company}</p>
          </div>
          {onToggleSave && (
            <button onClick={(e) => { e.stopPropagation(); onToggleSave(job); }} data-testid={`save-job-${job.id}`}
              className="shrink-0 rounded-md p-1.5 text-zinc-500 transition-colors hover:bg-[#27272A] hover:text-[#D4AF37]">
              {job.status ? <BookmarkCheck className="h-4 w-4 text-[#D4AF37]" /> : <Bookmark className="h-4 w-4" />}
            </button>
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1.5 text-xs text-zinc-500">
          {isNew && <span className="rounded-full bg-[#D4AF37] px-2 py-0.5 text-[10px] font-bold text-black" data-testid="new-badge">NEW</span>}
          <AtsBadge jobId={job.id} />
          {tailor && <TailorResumeButton job={job} compact />}
          <StatusBadge status={job.status} />
          {job.location && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>}
          {job.is_remote && <span className="flex items-center gap-1 text-emerald-400"><Wifi className="h-3 w-3" />Remote</span>}
          {job.employment_type && <span>{job.employment_type}</span>}
          {job.salary && <span className="font-mono text-[#F3C649]">{job.salary}</span>}
        </div>
        {job.description_snippet && <p className="mt-3 line-clamp-2 text-sm text-zinc-500">{job.description_snippet}</p>}
        <div className="mt-3 flex items-center gap-3 font-mono text-[10px] uppercase tracking-wider text-zinc-600">
          {job.posted_label && <span>{job.posted_label}</span>}
          {job.publisher && <span>via {job.publisher}</span>}
        </div>
        {findPeople && job.company && <div className="mt-3"><FindPeopleButton job={job} compact /></div>}
      </div>
    </div>
  </article>
);
